/**
 * MovesStrip — Persistent "Next 3 Moves" strip that sits above the chat input.
 * Lifts the same proactive guide cards used by ProactiveWelcome into a compact,
 * always-visible row so users keep an answer to "what could I do here?" even
 * mid-conversation.
 *
 * Dismissible per-session via the X button. Hidden globally when the user
 * disables `useSkerpaPreferencesStore.showMovesStrip`.
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useProactiveGuide } from '@/hooks/useProactiveGuide'
import { useSkerpaPreferencesStore } from '@/stores/skerpaPreferencesStore'
import type { GuideCard, GuideDomain } from '@ispot/shared'

const DOMAIN_DOT: Record<GuideDomain, string> = {
  market: 'bg-market',
  business: 'bg-business',
  solution: 'bg-solution',
  general: 'bg-warm-300',
}

interface MovesStripProps {
  onSend: (text: string) => void
  isFullscreen?: boolean
  suppress?: boolean  // hide when message-level pills are already visible
}

export function MovesStrip({ onSend, isFullscreen, suppress }: MovesStripProps) {
  const { cards, isReady } = useProactiveGuide()
  const showStrip = useSkerpaPreferencesStore(s => s.showMovesStrip)
  const [dismissed, setDismissed] = useState(false)

  if (!showStrip || dismissed || !isReady || cards.length === 0 || suppress) return null

  const visible = cards.slice(0, 3)

  return (
    <div className={cn('border-t border-border-subtle bg-warm-50/30 px-4 py-1.5', isFullscreen && 'px-8 mx-auto w-full max-w-3xl')}>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 flex flex-wrap gap-1 min-w-0">
          {visible.map(card => (
            <MoveButton key={card.id} card={card} onClick={() => onSend(card.prompt)} />
          ))}
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Hide suggestions"
          title="Hide suggestions"
          className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-text-muted hover:text-text hover:bg-warm-100 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

function MoveButton({ card, onClick }: { card: GuideCard; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={card.description}
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white border border-border-subtle text-text-muted hover:bg-warm-100 hover:text-text-secondary transition-colors leading-tight max-w-full"
    >
      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', DOMAIN_DOT[card.domain])} />
      <span className="truncate">{card.title}</span>
    </button>
  )
}
